import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private savedScrollPosition: number | null = null;

  /**
   * Save current scroll position for later restoration
   */
  saveScrollPosition(): void {
    this.savedScrollPosition =
      window.scrollY ||
      document.documentElement.scrollTop ||
      0;
  }

  /**
   * Restore previously saved scroll position
   * @param delay - Optional delay in milliseconds before restoring scroll
   */
  restoreScrollPosition(delay: number = 0): void {
    if (this.savedScrollPosition !== null) {
      setTimeout(() => {
        window.scrollTo(0, this.savedScrollPosition);
        this.savedScrollPosition = null;
      }, delay);
    }
  }

  /**
   * Clear saved scroll position without restoring
   */
  clearSavedScrollPosition(): void {
    this.savedScrollPosition = null;
  }

  /**
   * Generic method to scroll to any element and optionally focus it
   * @param selector - CSS selector for target element
   * @param options - Configuration options
   */
  scrollToElement(
    selector: string,
    options: {
      behavior?: 'smooth' | 'instant' | 'auto';
      block?: 'start' | 'center' | 'end' | 'nearest';
      focusAfterScroll?: boolean;
      focusDelay?: number;
      customFocusTarget?: string;
      offsetY?: number; // Custom offset in pixels (negative scrolls above element)
    } = {}
  ): boolean {
    const {
      behavior = 'smooth',
      block = 'start',
      focusAfterScroll = true,
      focusDelay = 300,
      customFocusTarget,
      offsetY = 0,
    } = options;

    const targetElement = document.querySelector(selector) as HTMLElement;

    if (!targetElement) {
      console.warn(
        `NavigationService: Element with selector "${selector}" not found`
      );
      return false;
    }

    // Use custom scroll implementation when offset is specified
    if (offsetY !== 0) {
      this.scrollToElementWithOffset(targetElement, offsetY, behavior);
    } else {
      // Use standard scrollIntoView when no offset needed
      targetElement.scrollIntoView({ behavior, block });
    }

    // Handle focus if requested
    if (focusAfterScroll) {
      setTimeout(() => {
        const focusTarget = customFocusTarget
          ? (document.querySelector(customFocusTarget) as HTMLElement)
          : targetElement;

        if (focusTarget) {
          focusTarget.focus();
        }
      }, focusDelay);
    }

    return true;
  }

  /**
   * Skip to main content (offers section) with 15px offset above
   */
  skipToMainContent(): boolean {
    return this.scrollToElement('.offer', {
      behavior: 'smooth',
      block: 'start',
      focusAfterScroll: true,
      focusDelay: 300,
      offsetY: -15, // Scroll 15px above the element
    });
  }

  /**
   * Scroll to element with custom offset - cross-browser compatible
   */
  private scrollToElementWithOffset(
    element: HTMLElement,
    offsetY: number,
    behavior: 'smooth' | 'instant' | 'auto' = 'smooth'
  ): void {
    // Get current scroll position
    const currentScroll =
      window.scrollY ||
      document.documentElement.scrollTop ||
      0;

    // Calculate target position
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top + currentScroll;
    const targetPosition = Math.max(0, elementTop + offsetY);

    // Animate scroll for smooth behavior, instant scroll otherwise
    if (behavior === 'smooth') {
      this.animateScrollTo(targetPosition);
    } else {
      window.scrollTo(0, targetPosition);
    }
  }

  /**
   * Custom smooth scroll animation - consistent across all browsers
   */
  private animateScrollTo(targetY: number): void {
    const startY =
      window.scrollY ||
      document.documentElement.scrollTop ||
      0;

    const distance = targetY - startY;
    const duration = 600;
    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth easing function for natural animation
      const easeInOutCubic = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const currentY = startY + distance * easeInOutCubic(progress);
      window.scrollTo(0, currentY);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }
}
